import type { TourSource, TourStep } from '../types';

export type TourPhase = 'idle' | 'starting' | 'waiting' | 'showing' | 'paused' | 'done';

export type EndReason = 'end-button' | 'escape' | 'paused-end' | 'never';

export interface TourMachineState {
  phase: TourPhase;
  tourId: string | null;
  /** Filtered steps plus the closing finish card. */
  steps: readonly TourStep[];
  index: number;
  /** Indices shown before the current one, so Back skips steps that were skipped. */
  history: readonly number[];
  /** Target found for the current step, or null for a centred card. */
  target: string | null;
  /** Body key suffix of the variant that was found. */
  copy: string;
  /** The step's target never appeared; the card says so instead of pointing at nothing. */
  missing: boolean;
  source: TourSource | null;
  params: Record<string, string>;
  outcome: 'completed' | 'dismissed' | null;
  endReason: EndReason | null;
  startedAt: number;
}

export const initialTourState: TourMachineState = {
  phase: 'idle',
  tourId: null,
  steps: [],
  index: 0,
  history: [],
  target: null,
  copy: 'body',
  missing: false,
  source: null,
  params: {},
  outcome: null,
  endReason: null,
  startedAt: 0,
};

export type TourAction =
  | {
      type: 'start';
      tourId: string;
      steps: readonly TourStep[];
      source: TourSource;
      params: Record<string, string>;
      at: number;
    }
  | { type: 'ready' }
  | { type: 'found'; target: string | null; copy?: string }
  | { type: 'timeout' }
  | { type: 'next' }
  | { type: 'back' }
  | { type: 'pause' }
  | { type: 'resume' }
  | { type: 'end'; reason: EndReason }
  | {
      type: 'restore';
      tourId: string;
      steps: readonly TourStep[];
      index: number;
      history: readonly number[];
      source: TourSource | null;
      params: Record<string, string>;
      paused: boolean;
      at: number;
    }
  | { type: 'reset' };

export const FINISH_STEP: TourStep = { id: 'finish', kind: 'finish' };

/** Appends the closing card every tour ends with. */
export function withFinish(steps: readonly TourStep[]): TourStep[] {
  return [...steps.filter((step) => step.kind !== 'finish'), FINISH_STEP];
}

const active = (state: TourMachineState) => state.phase === 'waiting' || state.phase === 'showing';

function moveTo(state: TourMachineState, index: number, history: readonly number[]) {
  return {
    ...state,
    phase: 'waiting' as const,
    index,
    history,
    target: null,
    copy: 'body',
    missing: false,
  };
}

export function tourReducer(state: TourMachineState, action: TourAction): TourMachineState {
  switch (action.type) {
    case 'start':
      return {
        ...initialTourState,
        phase: 'starting',
        tourId: action.tourId,
        steps: action.steps,
        source: action.source,
        params: action.params,
        startedAt: action.at,
      };
    case 'restore':
      return {
        ...initialTourState,
        phase: action.paused ? 'paused' : 'waiting',
        tourId: action.tourId,
        steps: action.steps,
        index: Math.min(action.index, action.steps.length - 1),
        history: action.history,
        source: action.source,
        params: action.params,
        startedAt: action.at,
      };
    case 'ready':
      return state.phase === 'starting' ? moveTo(state, 0, []) : state;
    case 'found':
      if (state.phase !== 'waiting') return state;
      return {
        ...state,
        phase: 'showing',
        target: action.target,
        copy: action.copy ?? 'body',
        missing: false,
      };
    case 'timeout': {
      if (state.phase !== 'waiting') return state;
      const step = state.steps[state.index];
      if (step?.optional) {
        // A skipped step never enters history, so Back from the next step jumps over it.
        return state.index + 1 < state.steps.length
          ? moveTo(state, state.index + 1, state.history)
          : { ...state, phase: 'done', outcome: 'completed' };
      }
      return { ...state, phase: 'showing', target: null, missing: true };
    }
    case 'next':
      if (!active(state)) return state;
      if (state.index + 1 >= state.steps.length) {
        return { ...state, phase: 'done', outcome: 'completed' };
      }
      return moveTo(state, state.index + 1, [...state.history, state.index]);
    case 'back': {
      if (!active(state) || state.history.length === 0) return state;
      const previous = state.history[state.history.length - 1] ?? 0;
      return moveTo(state, previous, state.history.slice(0, -1));
    }
    case 'pause':
      return active(state) ? { ...state, phase: 'paused' } : state;
    case 'resume':
      return state.phase === 'paused' ? moveTo(state, state.index, state.history) : state;
    case 'end':
      if (state.phase === 'idle' || state.phase === 'done') return state;
      return { ...state, phase: 'done', outcome: 'dismissed', endReason: action.reason };
    case 'reset':
      return initialTourState;
  }
}
